import React from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';

import {
  GetAreasByZoneId,
  GetAreasWorldMap,
  GetResourceValue,
  UpdateAreaAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import PopupMenu from '../../popupMenu';
import { MapConfigStore } from '../../../pages/game-designer';
import { AreaEntity } from '../../../types/game-document/entities';
import { GameDocumentContext } from '../../../contexts/game-document';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

const zoneAreaMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

export const Areas: React.FC<{
  searchContentText: string;
  handleAreaMenuSelected: (id: string, menu: MenuList) => void;
}> = ({ searchContentText, handleAreaMenuSelected }) => {
  const { zoneId, gameId } = useParams();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);
  const { gameDocument } = state;

  const areas = React.useMemo(() => {
    if (!gameDocument) return [];

    if (zoneId) {
      return GetAreasByZoneId(gameDocument, zoneId);
    } else {
      return GetAreasWorldMap(gameDocument);
    }
  }, [gameDocument, zoneId]);

  const filteredAreas = React.useMemo(() => {
    return areas.filter((area) => {
      const areaTitle = GetResourceValue(gameDocument!, area.titleResId ?? '');
      return areaTitle.toLowerCase().includes(searchContentText.toLowerCase());
    });
  }, [areas, gameDocument, searchContentText]);

  const showArea = (area: AreaEntity, isVisible: boolean) => {
    if (gameDocument) {
      area.isVisible = isVisible;
      const result = UpdateAreaAsync(gameDocument, area.id, area);
      setState((prev) => UpdateGameDocState(prev, result));
    }
  };

  if (
    filteredAreas.length === 0 ||
    (searchContentText && filteredAreas.length === 0)
  )
    return null;

  return (
    <PanelBar
      onSelect={() => {
        setMapConfig((prev) => ({
          ...prev,
          mapContentAreas: !prev.mapContentAreas
        }));
      }}>
      <PanelBarItem
        id={'area-panel-bar'}
        expanded={mapConfig.mapContentAreas}
        className={'panel-bar-custom'}
        title={
          <div className={'d-flex gap-2 align-items-center'}>
            <span
              className="material-symbols-outlined"
              style={{
                color: '#FEB333'
              }}>
              crop_free
            </span>
            Areas
          </div>
        }>
        {filteredAreas.map((area, index) => (
          <div
            className={
              'd-flex w-100 justify-content-between align-items-center mt-1 gap-2'
            }
            key={area.id}>
            <div
              id={`areaList-${area.boundary.id}`}
              className={'card w-100 border-0'}
              style={{
                backgroundColor: '#FEB333',
                borderColor: '#e5633d',
                borderWidth: '3px'
              }}>
              <div
                className={
                  'card-body p-2 d-flex w-100 justify-content-between align-items-center gap-2'
                }>
                <div
                  className={'d-flex gap-2 text-light align-items-center'}
                  style={{
                    minWidth: 0,
                    flex: 1
                  }}>
                  <span className={'material-symbols-outlined flex-shrink-0'}>
                    crop_free
                  </span>
                  <span
                    title={GetResourceValue(
                      state.gameDocument!,
                      area.titleResId ?? ''
                    )}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0
                    }}>
                    {GetResourceValue(
                      state.gameDocument!,
                      area.titleResId ?? ''
                    )}
                  </span>
                </div>
                <div className={'flex-shrink-0'}>
                  <PopupMenu
                    buttonColor="white"
                    id={index as number}
                    menus={zoneAreaMenu}
                    onMenuSelected={(_, mn) =>
                      handleAreaMenuSelected(area.id, mn)
                    }
                    iconClass={'k-icon k-i-more-vertical h5 mb-0 text-light'}
                  />
                </div>
              </div>
            </div>

            <span
              onClick={() => showArea(area, !area.isVisible)}
              className={`${area.isVisible ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
              {area.isVisible ? 'visibility' : 'visibility_off'}
            </span>
          </div>
        ))}
      </PanelBarItem>
    </PanelBar>
  );
};
